/**
 * Marketplace client for fetching and caching marketplace.json (Contract 3
 * validation + client-side caching), plus the tolerant fetch for the sibling
 * adoption.json artifact (Contract 7) that the web joins on at read time.
 */

import type { MarketplaceCatalog } from "@skills-marketplace/contracts/contract-3-marketplace-json";
import type { AdoptionCatalog, AdoptionEntry } from "@skills-marketplace/contracts/contract-7-adoption";
import { ADOPTION_ARTIFACT_KEY, ADOPTION_WINDOW_DAYS } from "@skills-marketplace/contracts/contract-7-adoption";
import type { MarketplaceClientConfig, MarketplaceClientError, CatalogCache } from "../types/index";
import { env } from "./env";
import { reportEvent } from "./newrelic";

// Bumped to 1.4 for the adoption read-time join (#263).
//
// Strictly speaking the cached SHAPE is unchanged — writeCache stores only the
// validated Contract 3 payload, and mergeAdoption never mutates its input, so
// adoption never reaches the cache and a 1.3-cached catalog would merge fine. The
// bump is a deliberate belt-and-braces eviction so no user can be served a catalog
// cached by a pre-adoption build. Cost is one forced cache miss per user on deploy.
const CACHE_VERSION = "1.4";
const CACHE_KEY_PREFIX = "skills_marketplace_catalog_v";
const CACHE_KEY = `${CACHE_KEY_PREFIX}${CACHE_VERSION}`;
const DEFAULT_CACHE_EXPIRY_MS = 60000; // 60 seconds

export class MarketplaceClient {
  private config: MarketplaceClientConfig;

  constructor(config: MarketplaceClientConfig) {
    this.config = {
      cacheExpiryMs: DEFAULT_CACHE_EXPIRY_MS,
      ...config,
    };

    if (!config.catalogUrl) {
      throw new Error(
        "MarketplaceClient requires catalogUrl in config. " +
        "Provide REACT_APP_CATALOG_URL environment variable."
      );
    }

    this.evictStaleCacheVersions();
  }

  /**
   * Drop catalogs cached under a previous CACHE_VERSION.
   *
   * clearCache() only removes the current key, so without this every version bump
   * strands a full catalog (megabytes, at 1500+ entries) in localStorage forever,
   * walking users toward the ~5 MB origin quota. Hitting it makes writeCache throw
   * QuotaExceededError, which is swallowed into a console.warn — caching would
   * silently stop working with no other symptom.
   */
  private evictStaleCacheVersions(): void {
    try {
      const stale: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(CACHE_KEY_PREFIX) && key !== CACHE_KEY) stale.push(key);
      }
      // Collected first: removing during iteration reindexes the store.
      for (const key of stale) localStorage.removeItem(key);
    } catch (error) {
      console.warn("Failed to evict stale catalog caches:", error);
    }
  }

  /**
   * Fetch marketplace catalog from CloudFront
   * Attempts to use cached version if available and not expired
   */
  async getCatalog(): Promise<MarketplaceCatalog> {
    // Try cache first
    const cached = this.readCache();
    if (cached) {
      return cached.catalog;
    }

    // Fetch from remote
    const catalog = await this.fetchCatalog();

    // Cache the result
    this.writeCache(catalog);

    return catalog;
  }

  /**
   * Force refresh: Bypass cache and fetch from remote
   */
  async refreshCatalog(): Promise<MarketplaceCatalog> {
    this.clearCache();
    return this.getCatalog();
  }

  /**
   * Internal: Fetch from remote URL
   *
   * Attaches a Bearer token so the CloudFront Lambda@Edge auth gate on
   * /marketplace.json allows the request.  In dev mode (no Entra creds),
   * the header is omitted so local development still works without a token.
   */
  private async fetchCatalog(): Promise<MarketplaceCatalog> {
    const headers: Record<string, string> = {
      "Accept": "application/json",
    };

    if (this.config.getToken) {
      try {
        const token = await this.config.getToken();
        if (token) {
          headers["Authorization"] = `Bearer ${token}`;
        }
      } catch (error) {
        throw this.createError(
          "network",
          `Failed to acquire auth token for marketplace catalog: ${error instanceof Error ? error.message : String(error)}`
        );
      }
    }

    let response: Response;
    try {
      response = await fetch(this.config.catalogUrl, {
        method: "GET",
        cache: "no-cache",
        headers,
      });
    } catch (error) {
      throw this.createError(
        "network",
        `Network error fetching marketplace catalog: ${error instanceof Error ? error.message : String(error)}`
      );
    }

    if (response.status === 401) {
      throw this.createError(
        "network",
        "Authentication required: sign in to view the marketplace catalog (401)"
      );
    }

    if (!response.ok) {
      throw this.createError(
        "network",
        `Failed to fetch marketplace.json: ${response.status} ${response.statusText}`
      );
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch (error) {
      throw this.createError(
        "parse",
        `Failed to parse marketplace.json as JSON: ${error instanceof Error ? error.message : String(error)}`
      );
    }

    return this.validateCatalog(json);
  }

  /**
   * Internal: Validate catalog against Contract 3 schema
   */
  private validateCatalog(json: unknown): MarketplaceCatalog {
    if (!json || typeof json !== "object") {
      throw this.createError("parse", "Catalog is not a valid JSON object");
    }

    const catalog = json as Record<string, unknown>;

    // Validate required fields
    if (!catalog.schemaVersion) {
      throw this.createError(
        "validation",
        "Catalog missing schemaVersion field"
      );
    }

    const majorVersion = String(catalog.schemaVersion).split(".")[0];
    if (majorVersion !== "1") {
      throw this.createError(
        "validation",
        `Unsupported schema major version: ${majorVersion}. Expected 1`
      );
    }

    if (typeof catalog.totalPlugins !== "number") {
      throw this.createError(
        "validation",
        "Catalog missing or invalid totalPlugins field"
      );
    }

    if (!Array.isArray(catalog.plugins)) {
      throw this.createError(
        "validation",
        "Catalog missing or invalid plugins field (expected array)"
      );
    }

    // Validate plugin structure: required string fields and keywords array
    const requiredStringFields = ["displayName", "pluginName", "description", "version", "source", "orgOrOwner", "repoName", "repoUrl", "lastCommitSha", "lastPushedBy", "lastUpdatedAt", "firstDiscoveredAt"];
    for (let i = 0; i < catalog.plugins.length; i++) {
      const plugin = catalog.plugins[i] as Record<string, unknown>;
      for (const field of requiredStringFields) {
        if (typeof plugin[field] !== "string") {
          throw this.createError(
            "validation",
            `Plugin at index ${i}: field "${field}" must be a string, got ${typeof plugin[field]}`
          );
        }
      }
      if (!Array.isArray(plugin.keywords)) {
        throw this.createError(
          "validation",
          `Plugin at index ${i}: field "keywords" must be an array`
        );
      }
      if (plugin.skills !== undefined && plugin.skills !== null) {
        if (!Array.isArray(plugin.skills)) {
          plugin.skills = undefined;
        } else {
          plugin.skills = (plugin.skills as unknown[]).filter(
            (s): s is { name: string; description?: string } =>
              typeof s === "object" && s !== null && typeof (s as Record<string, unknown>).name === "string" && (s as Record<string, unknown>).name !== "",
          );
          if ((plugin.skills as unknown[]).length === 0) plugin.skills = undefined;
        }
      }

      // Derive extensionType for pre-schema-1.3 records where the field is absent.
      // Mirrors the catalog-builder's deriveExtensionType logic so all components
      // can rely on extensionType being present without individual fallbacks.
      if (plugin.extensionType !== "plugin" && plugin.extensionType !== "skill") {
        const skills = Array.isArray(plugin.skills) ? plugin.skills as Array<{ name: string }> : [];
        if (skills.length > 0) {
          // Any container with embedded skills is a plugin — including a
          // single-skill plugin whose only skill shares its name. Server-built
          // catalogs additionally emit a synthesized "skill" twin for that case
          // (#329); this fallback can only relabel the single legacy entry it
          // sees, so it labels it "plugin" (its honest primary type).
          plugin.extensionType = "plugin";
        } else {
          // No skills[] and no skillPath: nothing structural distinguishes a
          // standalone single-skill repo from a bare plugin, so fall back to
          // claude-chat membership as a heuristic — same rule as catalog-builder's
          // deriveExtensionType, plus a tolerance for the un-remapped legacy
          // "claude-desktop" value (the server only ever sees it post-remap, but a
          // pre-migration catalog fetched here may still carry it). (Chat now runs
          // plugins too, so a bare chat-compatible plugin can be mislabeled "skill"
          // — known limitation.)
          const clients = Array.isArray(plugin.supportedClients) ? plugin.supportedClients as string[] : [];
          plugin.extensionType = clients.includes("claude-chat") || clients.includes("claude-desktop")
            ? "skill"
            : "plugin";
        }
      }
    }

    return catalog as unknown as MarketplaceCatalog;
  }

  /**
   * Internal: Read from localStorage cache
   */
  private readCache(): CatalogCache | null {
    try {
      const cached = localStorage.getItem(CACHE_KEY);
      if (!cached) return null;

      const data = JSON.parse(cached) as CatalogCache;
      const age = Date.now() - data.retrievedAt;

      // Check if cache is still valid
      if (age < (this.config.cacheExpiryMs || DEFAULT_CACHE_EXPIRY_MS)) {
        return data;
      }

      // Cache expired
      this.clearCache();
      return null;
    } catch (error) {
      console.warn("Failed to read cache:", error);
      this.clearCache();
      return null;
    }
  }

  /**
   * Internal: Write to localStorage cache
   */
  private writeCache(catalog: MarketplaceCatalog): void {
    try {
      const data: CatalogCache = {
        catalog,
        retrievedAt: Date.now(),
      };
      localStorage.setItem(CACHE_KEY, JSON.stringify(data));
    } catch (error) {
      console.warn("Failed to write cache:", error);
    }
  }

  /**
   * Internal: Clear cache
   */
  private clearCache(): void {
    try {
      localStorage.removeItem(CACHE_KEY);
    } catch (error) {
      console.warn("Failed to clear cache:", error);
    }
  }

  /**
   * Internal: Create an error object
   */
  private createError(type: MarketplaceClientError["type"], message: string): MarketplaceClientErrorImpl {
    return new MarketplaceClientErrorImpl(type, message);
  }
}

/**
 * Custom error class for marketplace client errors
 */
export class MarketplaceClientErrorImpl extends Error implements MarketplaceClientError {
  type: MarketplaceClientError["type"];
  message: string;
  details?: Record<string, unknown>;

  constructor(type: MarketplaceClientError["type"], message: string, details?: Record<string, unknown>) {
    super(message);
    this.type = type;
    this.message = message;
    this.details = details;
    this.name = "MarketplaceClientError";
  }
}

// ── Adoption (Contract 7) ──────────────────────────────────────────────────────
//
// Adoption is a SEPARATE artifact joined onto the catalog at read time, so this
// fetch is deliberately not a MarketplaceClient method: it has different failure
// semantics. Every failure mode — missing artifact, non-2xx, unparseable body,
// wrong schema, network rejection, token acquisition failure — resolves to null
// with a warning. It MUST NEVER throw and MUST NEVER fail a catalog load; that
// is the "strictly additive" guarantee. See contracts/contract-7-adoption.ts.
//
// Observability: the catalog path reports failures via reportError, which feeds a
// PAGING JavaScriptError alert. That is wrong for adoption, where failure is
// routine and non-blocking, so these branches use reportEvent (a queryable
// PageAction) instead — a signal without a page. Attributes stay bounded per
// reportEvent's contract: an enum reason, a status code, a count. Never a URL,
// which can carry a host and query string.
//
// The JSON-parse branch deliberately does NOT report. In a deployed environment
// that is the routine absent-artifact path (CloudFront maps 404 to /index.html
// with a 200, so an absent artifact arrives as HTML), and reporting it would emit
// for 100% of users on every load until #264 ships. Worth revisiting once a real
// artifact exists everywhere and a parse failure means genuine corruption.
const ADOPTION_FETCH_FAILED = "adoptionFetchFailed";

/**
 * Report an adoption failure without ever becoming one.
 *
 * These calls live inside fetchAdoption's failure branches — including its outermost
 * catch — so a throw here would escape the function and break the never-throws
 * guarantee that the whole "strictly additive" property rests on. reportEvent
 * already guards internally, but the guarantee should not depend on that, or on a
 * test double happening to provide the export.
 */
function reportAdoptionFailure(attributes: Record<string, string | number>): void {
  try {
    reportEvent(ADOPTION_FETCH_FAILED, attributes);
  } catch {
    // Reporting is best-effort; losing a metric must never cost the caller.
  }
}

export interface AdoptionFetchConfig {
  /**
   * Hard override — wins over everything. For tests and any caller that genuinely
   * knows the URL.
   */
  adoptionUrl?: string;
  /**
   * A *derived* default, ranked BELOW `REACT_APP_ADOPTION_URL`. The provider derives
   * one from its configured catalogUrl; if that outranked the env var, a caller who
   * passed a catalogUrl AND set the documented escape hatch would silently get the
   * derivation instead, with a console.warn as their only clue.
   */
  adoptionUrlDefault?: string;
  /**
   * Same Bearer token the catalog fetch uses.
   *
   * NOTE: /adoption.json IS gated, by its own ordered behavior in
   * infra/modules/catalog-web/cloudfront.tf carrying the same Lambda@Edge
   * viewer-request association as /marketplace.json. Sending the token is
   * therefore required, not merely forward-looking. In ephemeral previews the
   * bucket policy denies the read outright and the artifact does not exist, so
   * the request degrades to the empty state.
   */
  getToken?: () => Promise<string | null>;
}

/**
 * Resolve the adoption artifact URL from the catalog URL.
 *
 * Both artifacts sit at the catalog bucket root, so adoption.json is always a
 * sibling of marketplace.json. Deriving it means previews and every persistent
 * environment work with no extra configuration (previews simply 404 — the
 * refresh job is persistent-environments-only — and render the empty state).
 */
export function adoptionUrlFor(catalogUrl: string): string {
  const [path] = catalogUrl.split(/[?#]/);
  const lastSlash = path.lastIndexOf("/");
  const dir = lastSlash >= 0 ? path.slice(0, lastSlash + 1) : "";
  return `${dir}${ADOPTION_ARTIFACT_KEY}`;
}

/**
 * Fetch the adoption artifact. Resolves to null whenever it is unavailable or
 * unusable, rather than throwing.
 */
export async function fetchAdoption(config: AdoptionFetchConfig = {}): Promise<AdoptionCatalog | null> {
  // Precedence: explicit override, then the env escape hatch, then a caller-derived
  // default, then the sibling of the configured catalog URL.
  const url =
    config.adoptionUrl ||
    env.REACT_APP_ADOPTION_URL ||
    config.adoptionUrlDefault ||
    adoptionUrlFor(env.REACT_APP_CATALOG_URL || "/marketplace.json");

  try {
    const headers: Record<string, string> = { "Accept": "application/json" };

    if (config.getToken) {
      // A token failure degrades adoption to absent; it must not surface as a
      // catalog error the way the equivalent failure does in fetchCatalog().
      try {
        const token = await config.getToken();
        if (token) headers["Authorization"] = `Bearer ${token}`;
      } catch (error) {
        console.warn(`Adoption data unavailable: could not acquire auth token for ${url}:`, error);
        reportAdoptionFailure({ reason: "token" });
        return null;
      }
    }

    const response = await fetch(url, { method: "GET", cache: "no-cache", headers });

    if (!response.ok) {
      // A missing artifact is the expected steady state before #264 ships, and in
      // ephemeral previews. Not an error condition. (Kept at warn deliberately: it
      // is the only signal that adoption isn't wired yet. Worth revisiting once the
      // artifact exists everywhere, when it stops being routine.)
      //
      // Heads up when diagnosing: in a DEPLOYED environment this branch does not
      // run for an absent artifact. The distribution maps both 403 and 404 to
      // /index.html with response_code 200 (catalog-web/cloudfront.tf), so a missing
      // adoption.json arrives as 200 text/html and lands in the "was not valid JSON"
      // branch below instead. Same null outcome, misleading hint.
      console.warn(`Adoption data unavailable: ${url} returned ${response.status} ${response.statusText}`);
      reportAdoptionFailure({ reason: "http", status: response.status });
      return null;
    }

    let json: unknown;
    try {
      json = await response.json();
    } catch (error) {
      console.warn(`Adoption data unavailable: ${url} was not valid JSON:`, error);
      return null;
    }

    return validateAdoption(json, url);
  } catch (error) {
    console.warn(`Adoption data unavailable: failed to fetch ${url}:`, error);
    reportAdoptionFailure({ reason: "transport" });
    return null;
  }
}

/**
 * Validate the artifact envelope, dropping individual entries that are unusable.
 *
 * Partial population is expected (coverage is ~42%), so a bad entry is skipped
 * rather than rejecting the whole artifact and losing the good ones.
 */
function validateAdoption(json: unknown, url: string): AdoptionCatalog | null {
  if (!json || typeof json !== "object" || Array.isArray(json)) {
    console.warn(`Adoption data unavailable: ${url} is not a JSON object`);
    return null;
  }

  const raw = json as Record<string, unknown>;

  const majorVersion = String(raw.schemaVersion ?? "").split(".")[0];
  if (majorVersion !== "1") {
    console.warn(
      `Adoption data unavailable: ${url} has unsupported schema major version "${majorVersion}" (expected 1)`,
    );
    reportAdoptionFailure({ reason: "schemaVersion" });
    return null;
  }

  if (!raw.entries || typeof raw.entries !== "object" || Array.isArray(raw.entries)) {
    console.warn(`Adoption data unavailable: ${url} has a missing or invalid "entries" object`);
    reportAdoptionFailure({ reason: "envelope" });
    return null;
  }

  // Null-prototype: installNames are free-form slugs, so a plugin genuinely named
  // "constructor" or "toString" would otherwise hit an inherited property on a
  // plain object and merge a bogus {activeUsers30d: undefined}.
  const entries: Record<string, AdoptionEntry> = Object.create(null);
  let skipped = 0;
  // A few offending pairs, so the COALESCE(count, 0) case this validator exists to
  // catch is diagnosable from a log line without a local repro. Capped, and
  // installNames are public slugs — no user content.
  const skippedSamples: string[] = [];

  for (const [installName, value] of Object.entries(raw.entries as Record<string, unknown>)) {
    const activeUsers30d = (value as AdoptionEntry | null)?.activeUsers30d;
    // >= 1 and integral. The mart only records skills that were actually used, so a
    // 0 (or a fractional count) is a producer bug, not a measurement — admitting it
    // would manufacture the "measured zero" state Contract 7 forbids. Dropping it
    // keeps absence as the SINGLE empty state.
    if (typeof activeUsers30d !== "number" || !Number.isInteger(activeUsers30d) || activeUsers30d < 1) {
      skipped++;
      if (skippedSamples.length < 3) {
        // JSON.stringify renders Infinity and NaN as "null", which hides the actual
        // problem. String() keeps them readable; JSON.stringify keeps "12" visibly a
        // string rather than looking like the number 12.
        const shown =
          typeof activeUsers30d === "number" && !Number.isFinite(activeUsers30d)
            ? String(activeUsers30d)
            : JSON.stringify(activeUsers30d);
        skippedSamples.push(`${installName}=${shown}`);
      }
      continue;
    }
    const rawSharedNameUsage = (value as AdoptionEntry | null)?.sharedNameUsage;
    entries[installName] = {
      activeUsers30d,
      ...(rawSharedNameUsage === true ? { sharedNameUsage: true } : {}),
    };
  }

  if (skipped > 0) {
    console.warn(
      `Adoption data: skipped ${skipped} entr${skipped === 1 ? "y" : "ies"} in ${url} ` +
      `with an invalid activeUsers30d (e.g. ${skippedSamples.join(", ")})`,
    );
    reportAdoptionFailure({ reason: "entriesSkipped", skipped });
  }

  return {
    schemaVersion: raw.schemaVersion === "1.1" ? "1.1" : "1.0",
    generatedAt: typeof raw.generatedAt === "string" ? raw.generatedAt : "",
    windowDays: typeof raw.windowDays === "number" ? raw.windowDays : ADOPTION_WINDOW_DAYS,
    dataThroughDate: typeof raw.dataThroughDate === "string" ? raw.dataThroughDate : "",
    entries,
  };
}

export function createMarketplaceClient(config?: Partial<MarketplaceClientConfig>): MarketplaceClient {
  const catalogUrl = env.REACT_APP_CATALOG_URL || "/marketplace.json";
  const fullConfig: MarketplaceClientConfig = {
    catalogUrl,
    ...config,
  };
  return new MarketplaceClient(fullConfig);
}
